<script setup lang="ts">
import type { CuratedList } from '~~/core/entities/CuratedList';
import type { NuxtListPropertiesApiReturn } from '~~/server/api/properties/index.get';

import { useEvents } from '~/composables/events/useEvents';
import { nuxtCollectionService } from '~~/adapters/services/nuxt/nuxtCollectionService';
import WheelGesturesPlugin from 'embla-carousel-wheel-gestures';
import { ref } from 'vue';

import {
	Carousel,
	CarouselContent,
	CarouselGenericAction,
	CarouselItem,
	CarouselItemSelector
} from '~/components/ui/carousel';

const { data, status: curatedListsStatus } = useAsyncData<{
	collections: CuratedList[];
}>(
	`home:collections:all`,
	async () => {
		const [collections] = await nuxtCollectionService.getMany();

		return { collections: collections || [] };
	},
	{
		default: () => ({ collections: [] }),
		server: true,
		lazy: false,
		immediate: true,
		getCachedData: (key, nuxtApp) =>
			nuxtApp.payload.data[key] ?? nuxtApp.static.data[key]
	}
);

const isFetching = computed<boolean>(
	() =>
		curatedListsStatus.value === 'pending' &&
		data.value.collections.length === 0
);

function getCollectionDetailsUrl(collection: CuratedList) {
	return `/colecao/${collection.slug}`;
}

const carouselContainer = ref<InstanceType<typeof Carousel> | null>(null);
const carouselItemSelectorContainer = ref<HTMLDivElement | null>(null);

const {
	currentCarouselItemIndex,
	scrollableSlidesNumber,
	carouselDynamicItemWidth,
	scrollTo
} = useCarouselControl({
	carouselContainerRef: carouselContainer,
	carouselItemSelectorContainerRef: carouselItemSelectorContainer,
	cyclic: true
});

function handlePreviousSlideArrowClick(): void {
	scrollTo(currentCarouselItemIndex.value - 1);
}

function handleNextSlideArrowClick(): void {
	scrollTo(currentCarouselItemIndex.value + 1);
}
</script>

<template>
	<section class="w-full bg-white text-primary">
		<LazyContainer
			class="max-w-full! flex-1 overflow-hidden border border-white-dark-2 px-0 pt-11"
			main-class="flex flex-col gap-6"
		>
			<header class="flex flex-col items-start gap-1 px-5 md:px-0">
				<h2
					class="font-matter-sq text-[1.75rem] font-normal leading-normal tracking-[-0.035rem] text-primary"
				>
					Curadorias Assinadas
				</h2>

				<p class="font-sans text-sm font-normal leading-normal text-primary">
					Imóveis selecionados por referências do design, arquitetura e
					lifestyle.
				</p>
			</header>

			<Carousel
				ref="carouselContainer"
				class="flex flex-col gap-5 xl:hidden"
				:plugins="[WheelGesturesPlugin() as any]"
				:opts="{
					align: 'center',
					slidesToScroll: 1,
					breakpoints: {
						'(min-width: 768px)': { align: 'start' }
					}
				}"
			>
				<CarouselContent class="flex gap-5 pl-5 md:pl-0">
					<template v-if="isFetching">
						<CarouselItem
							v-for="n in 3"
							:key="`curated-list-card-skeleton:${n}`"
							class="group relative basis-[90%] cursor-pointer overflow-hidden last:pr-5 md:basis-[48.5%] md:last:pr-0"
						>
							<Skeleton is-active variant="white" class="h-76 w-full" />
						</CarouselItem>
					</template>
					<template v-else>
						<CarouselItem
							v-for="collection in data.collections"
							:key="collection.slug"
							class="group relative w-auto shrink-0 grow basis-[90%] cursor-pointer overflow-hidden last:pr-5 md:basis-[48.5%] md:last:pr-0"
						>
							<CuratedListCard
								:curated-list="collection"
								:details-url="getCollectionDetailsUrl(collection)"
								variant="rich-content"
								class="h-full"
							/>
						</CarouselItem>
					</template>
				</CarouselContent>

				<div class="flex w-full items-center gap-5 px-5 md:px-0">
					<div
						ref="carouselItemSelectorContainer"
						class="flex w-full flex-wrap justify-end gap-2 md:gap-5"
					>
						<CarouselItemSelector
							v-for="(_, index) in scrollableSlidesNumber"
							:key="`selector:${index}`"
							:active="currentCarouselItemIndex === index"
							:delay="2000"
							:dynamic-width="carouselDynamicItemWidth"
							variant="dark"
							@click="scrollTo(index)"
						/>
					</div>

					<div class="flex gap-0">
						<CarouselGenericAction
							variant="dark"
							@click="handlePreviousSlideArrowClick"
						>
							<IconMaterialSymbolsChevronLeft class="size-7" />
						</CarouselGenericAction>
						<CarouselGenericAction
							variant="dark"
							@click="handleNextSlideArrowClick"
						>
							<IconMaterialSymbolsChevronRight class="size-7" />
						</CarouselGenericAction>
					</div>
				</div>
			</Carousel>

			<div class="hidden gap-5 xl:flex xl:items-stretch">
				<template v-if="isFetching">
					<Skeleton
						v-for="n in 3"
						:key="`lg:curated-list-card-skeleton:${n}`"
						is-active
						variant="white"
						class="h-81.5 w-1/3"
					/>
				</template>

				<template v-else>
					<CuratedListCard
						v-for="collection in data.collections"
						:key="collection.slug"
						:curated-list="collection"
						:details-url="getCollectionDetailsUrl(collection)"
						variant="rich-content"
						class="size-auto flex-1"
					/>
				</template>
			</div>
		</LazyContainer>
	</section>
</template>
